import React from 'react';
import { Box, Typography, Paper, ToggleButtonGroup, ToggleButton, Button, CircularProgress } from '@mui/material';
import { JsonSchemaFormEditor, RawJsonEditor } from '@src/components';
import { colors } from '@src/styles';
import { ResourceDefinitionDTO } from '@src/services/brain/modules/v1/resources/dto/brain-resources.get.response.dto';

export interface JsonViewerSectionProps {
  viewMode: 'schema' | 'raw';
  onViewModeChange: (_event: React.MouseEvent<HTMLElement>, newMode: 'schema' | 'raw' | null) => void;
  selectedDefinition: ResourceDefinitionDTO | null;
  schema: object;
  jsonEditorValue: string;
  jsonErrors: string[];
  isPublishing: boolean;
  canPublish: boolean;
  isPublishDisabled: boolean;
  onPublish: () => void;
  organizationId?: string;
}

const JsonViewerSection: React.FC<JsonViewerSectionProps> = ({
  viewMode,
  onViewModeChange,
  selectedDefinition,
  schema,
  jsonEditorValue,
  isPublishing,
  canPublish,
  isPublishDisabled,
  onPublish,
  organizationId,
}) => {
  return (
    <Paper
      sx={{
        border: `1px solid ${colors.brand.line}`,
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        width: '78%',
        flexDirection: 'column',
        gap: 2,
        backgroundColor: colors.brand.surface,
        backgroundImage: 'none',
        boxShadow: 'none',
        animation: 'fadeInUp 0.5s ease-out 0.3s both',
        '@keyframes fadeInUp': {
          '0%': {
            opacity: 0,
            transform: 'translateY(20px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        overflowY: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={onViewModeChange}
          size="small"
          sx={{
            padding: '3px',
            gap: '3px',
            borderRadius: '10px',
            backgroundColor: colors.background.primary,
            border: `1px solid ${colors.brand.line}`,
            '& .MuiToggleButton-root': {
              height: 28,
              paddingX: 1.5,
              border: 'none',
              borderRadius: '7px !important',
              color: colors.brand.textSecondary,
              fontFamily: "'Inter Tight', 'Inter', sans-serif",
              fontSize: '12.5px',
              fontWeight: 500,
              textTransform: 'none',
              '&.Mui-selected': {
                backgroundColor: colors.brand.surface,
                boxShadow: `inset 0 0 0 1px ${colors.brand.line}`,
                color: colors.primary.white,
                fontWeight: 600,
                '&:hover': { backgroundColor: colors.brand.surface },
              },
              '&:hover': { color: colors.primary.white, backgroundColor: 'transparent' },
            },
          }}
        >
          <ToggleButton value="schema">Formulário</ToggleButton>
          <ToggleButton value="raw">JSON</ToggleButton>
        </ToggleButtonGroup>

        <Button
          onClick={onPublish}
          disabled={isPublishing || !canPublish || isPublishDisabled}
          variant="contained"
          size="small"
          sx={{
            backgroundColor: colors.primary.yellow,
            color: colors.background.primary,
            fontSize: '0.75rem',
            padding: '6px 16px',
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: colors.primary.yellow,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              backgroundColor: colors.background.secondary,
              color: colors.primary.white,
              opacity: 0.5,
            },
          }}
        >
          {isPublishing ? <CircularProgress size={14} sx={{ color: colors.background.primary }} /> : 'Publicar'}
        </Button>
      </Box>

      {selectedDefinition ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            backgroundColor: colors.background.primary,
            border: `1px solid ${colors.brand.line}`,
            borderRadius: '10px',
            padding: '12px',
          }}
        >
          {viewMode === 'schema' ? (
            <JsonSchemaFormEditor
              schema={schema}
              formData={selectedDefinition.config}
              onChange={() => {}}
              readOnly
              organizationId={organizationId}
            />
          ) : (
            <RawJsonEditor value={jsonEditorValue} errors={[]} />
          )}
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: colors.primary.white, opacity: 0.5 }}>
            Selecione uma definição para visualizar
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default JsonViewerSection;
